import { fork, all, call, put, select, takeEvery } from 'redux-saga/effects'

// action
import { orderActions } from '@redux/store/main/order.store'
import { shipmentActions } from '@redux/store/main/shipment.store'
import { configActions } from '@redux/store/core/config.store'

// api
import { orderApi, seasonApi, ResType } from '@api'
import { isOk, errorText } from '@redux/saga/main/shipment.saga'

// lib
import { Logger } from '@utils'
import { writeBoFilter } from '@utils/BackofficeFilter'

import {
	OrderCreationType,
	OrderDeletionType,
	OrderUpdateType,
	OrderType,
	Order,
	Season,
	SeasonType,
} from '@schemas'

const apiGetOrder = async (query: {
	startDate: string
	endDate: string
	status: string
	searchKeyword: string
	seasonId: string
}): Promise<ResType | undefined> => {
	const res: ResType | undefined = await orderApi.getOrder({ query: query })
	return res
}

// API call to delete order — 화면 용어는 '취소'다 (ORDER_PLAN §2.4). 사유는 필수
async function deleteOrder(orderId: string, reason: string) {
	return await orderApi.deleteOrder({ params: { orderId }, body: { reason } })
}

// API call to update order
async function updateOrder({
	orderId,
	order,
}: {
	orderId: string
	order: OrderUpdateType
}) {
	return await orderApi.updateOrder({ params: { orderId }, body: order })
}

function* registerOrderSaga(action: {
	type: string
	payload: OrderCreationType
}): Generator<unknown, void, unknown> {
	try {
		yield call(orderApi.addOrder, {
			body: action.payload,
		})

		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(orderActions.REGISTER_ORDER_SUCCESS())
	} catch (error: any) {
		yield put(orderActions.REGISTER_ORDER_FAILURE(error.message))
	}
}

/**
 * 주문 취소 (자세히 모달) — `DELETE /order/:id` + 사유. 저장은 소프트 삭제이고 **돈은 건드리지 않는다.**
 * 환불이 필요하면 환불을 따로 한다 (ORDER_PLAN §2.4).
 */
function* deleteOrderSaga(action: {
	type: string
	payload: OrderDeletionType & { reason?: string }
}): Generator<unknown, void, unknown> {
	try {
		const res: ResType | undefined = (yield call(
			deleteOrder,
			action.payload.orderId,
			String(action.payload.reason ?? ''),
		)) as ResType | undefined

		if (isOk(res)) {
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
			yield put(orderActions.DELETE_ORDER_SUCCESS())
			yield put(
				configActions.TOAST({
					isShow: true,
					message: `주문 ${action.payload.orderId} — 취소했습니다.`,
				}),
			)
		} else {
			yield put(orderActions.DELETE_ORDER_FAILURE(errorText(res, '취소에 실패했습니다.')))
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '취소에 실패했습니다.'),
				}),
			)
		}
	} catch (error: any) {
		yield put(orderActions.DELETE_ORDER_FAILURE(error.message))
		yield put(configActions.TOAST({ isShow: true, message: '취소에 실패했습니다.' }))
	}
}

function* updateOrderSaga(action: {
	type: string
	payload: {
		orderId: string
		order: OrderUpdateType
	}
}): Generator<unknown, void, unknown> {
	try {
		yield call(updateOrder, {
			orderId: action.payload.orderId,
			order: action.payload.order,
		})
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(orderActions.UPDATE_ORDER_SUCCESS())
	} catch (error: any) {
		yield put(orderActions.UPDATE_ORDER_FAILURE(error.message))
	}
}

function* fetchOrderOptions(): Generator<unknown, void, unknown> {
	yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
}

/** 시즌 필터 옵션 — 연도 무관 전체 시즌(`GET /season?year=`). 빈 연도는 API 가 전체로 읽는다 */
function* fetchSeasonOptionsSaga() {
	try {
		const res: ResType | undefined = yield call(seasonApi.getSeason, {
			query: { year: '' },
		})
		if (res !== undefined && isOk(res)) {
			const list: SeasonType[] = (res.data as any[]).map(a => new Season(a))
			yield put(orderActions.SEASON_OPTIONS(list))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

// 페이지 초기화 관련
function* triggerInitOrderPage(): Generator<unknown, void, unknown> {
	try {
		yield call(fetchSeasonOptionsSaga)
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error: any) {
		Logger.warn(error)
	}
}

function* triggerOrderSearchSaga() {
	try {
		const searchStartDate: string = yield select(state => state.order.searchStartDate)
		const searchEndDate: string = yield select(state => state.order.searchEndDate)
		const searchStatus: string = yield select(state => state.order.searchStatus)
		const searchKeyword: string = yield select(state => state.order.searchKeyword)
		const searchMode: 'date' | 'season' = yield select(state => state.order.searchMode)
		const searchSeasonId: string = yield select(state => state.order.searchSeasonId)
		const searchChannel: string = yield select(state => state.order.searchChannel)
		yield put(orderActions.ORDER_STATUS('loading'))

		/*
			시즌 기준이면 기간을 보내지 않는다 — 날짜 피커는 화면에서 숨어 있는데 기본값(이번 달)이
			그대로 서버에 남아, '전체 시즌'(seasonId '')이 조용히 '이번 달'로 조회되던 것을 막는다.
			서버는 시즌·기간이 모두 비면 아무 조건도 걸지 않는다 (getOrder.ts).
		*/
		const isSeasonMode = searchMode === 'season'
		const query = {
			startDate: isSeasonMode ? '' : searchStartDate,
			endDate: isSeasonMode ? '' : searchEndDate,
			status: searchStatus,
			searchKeyword: searchKeyword,
			seasonId: isSeasonMode ? searchSeasonId : '',
			channel: searchChannel === 'all' ? '' : searchChannel,
		}
		const apiRes: ResType | undefined = (yield call(apiGetOrder, query)) as ResType

		if (apiRes.status === 200) {
			const newData: any[] = apiRes.data
			const newOrderList: OrderType[] = newData.map(aData => new Order(aData))

			yield put(orderActions.ORDER_LIST(newOrderList))

			if (newOrderList.length > 0) {
				yield put(orderActions.ORDER_STATUS('ok'))
			} else {
				yield put(orderActions.ORDER_STATUS('empty'))
			}
		} else {
			yield put(orderActions.ORDER_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 딥링크 한 건 — `/main/order/:orderId` 를 새로고침하거나 링크로 들어오면 목록에 그 주문이 없다.
 * 서버에 주문번호로 거는 조건이 없어(getOrder 의 searchKeyword 는 ID 를 보지 않는다)
 * 조건 없이(기간·시즌 빈 문자열) 한 번 읽고 번호로 고른다. 그리드의 검색 조건은 건드리지 않는다.
 */
function* fetchOrderOneSaga(action: { type: string; payload: string }) {
	try {
		const orderId = action.payload
		if (orderId === '') {
			yield put(orderActions.DETAIL_ORDER_STATUS('empty'))
			return
		}

		const res: ResType | undefined = yield call(apiGetOrder, {
			startDate: '',
			endDate: '',
			status: 'all',
			searchKeyword: '',
			seasonId: '',
		})

		if (isOk(res) && Array.isArray(res?.data)) {
			const found = (res?.data as any[]).find(aData => aData.id === orderId)
			if (found !== undefined) {
				yield put(orderActions.DETAIL_ORDER(new Order(found)))
				yield put(orderActions.DETAIL_ORDER_STATUS('ok'))
				return
			}
		}

		yield put(orderActions.DETAIL_ORDER(null))
		yield put(orderActions.DETAIL_ORDER_STATUS('empty'))
	} catch (error) {
		Logger.warn(error)
		yield put(orderActions.DETAIL_ORDER(null))
		yield put(orderActions.DETAIL_ORDER_STATUS('empty'))
	}
}

function* searchStartDateSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
function* searchEndDateSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
/**
 * 기준 · 시즌 · 상태 — 셋 다 이 사가가 받는다(아래 mainSaga 의 takeEvery 세 줄).
 * 재조회하면서 고른 값을 적어 둔다 — 다음에 로그인해 들어와도 같은 조건으로 열린다.
 */
function* searchStatusSaga() {
	try {
		const searchMode: 'date' | 'season' = yield select(state => state.order.searchMode)
		const searchSeasonId: string = yield select(state => state.order.searchSeasonId)
		const searchStatus: string = yield select(state => state.order.searchStatus)
		const searchChannel: string = yield select(state => state.order.searchChannel)

		writeBoFilter('order', {
			mode: searchMode,
			seasonId: searchSeasonId,
			status: searchStatus,
			channel: searchChannel,
		})

		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}
function* searchKeywordSaga() {
	try {
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
	} catch (error) {
		Logger.warn(error)
	}
}

function* selectedOrderIdsSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
		const orderList: OrderType[] = yield select(state => state.order.orderList)

		const selectedOrderList: OrderType[] = orderList.filter(anOrder =>
			selectedOrderIds.includes(anOrder.id),
		)
		const selectedStatusArr = selectedOrderList.reduce(
			(statusArr: string[], anOrder: OrderType) => {
				const newStatusArr = [...statusArr]

				if (!newStatusArr.includes(anOrder.status)) {
					newStatusArr.push(anOrder.status)
				}

				return newStatusArr
			},
			[],
		)

		yield put(orderActions.SELECTED_STATUS_ARR(selectedStatusArr))
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 주문확인 — 선택한 주문마다 PUT /order/:id/prepare. 끝나면 다시 검색하고 결과를 토스트로 알린다.
 * 한 건이 실패해도 멈추지 않고 끝까지 돌린 뒤, 첫 실패 사유를 한 줄로 보여준다.
 */
function* triggerOrderPreparingSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
		let okCount = 0
		let failText = ''

		for (const orderId of selectedOrderIds) {
			const res: ResType | undefined = yield call(orderApi.prepareOrder, {
				params: { orderId },
			})

			if (isOk(res)) {
				okCount += 1
			} else if (failText === '') {
				failText = errorText(res, '주문확인에 실패했습니다.')
			}
		}

		yield put(orderActions.SELECTED_ORDER_IDS([]))
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(
			configActions.TOAST({
				isShow: true,
				message: preparedToastText(selectedOrderIds, okCount, failText),
			}),
		)
	} catch (error) {
		Logger.warn(error)
		yield put(configActions.TOAST({ isShow: true, message: '주문확인에 실패했습니다.' }))
	}
}

/** 주문확인 결과 한 줄. 한 건이면 주문번호를, 여러 건이면 건수를 말한다 */
const preparedToastText = (orderIds: string[], okCount: number, failText: string) => {
	if (okCount === 0) {
		return failText !== '' ? failText : '주문확인에 실패했습니다.'
	}

	const done =
		orderIds.length === 1
			? `주문 ${orderIds[0]} — 배송 준비중으로 바뀌었습니다.`
			: `주문 ${okCount}건이 배송 준비중으로 바뀌었습니다.`

	return failText !== '' ? `${done} 나머지는 실패했습니다 — ${failText}` : done
}
/**
 * 라벨 — 한 건만. 송장이 있으면 저장된 라벨을 인쇄하고, 없으면 생성 모달을 연다.
 * 실제 동작은 shipment.saga 에 있다.
 */
/*
	라벨 생성 · 라벨 인쇄 · 배송 업데이트는 **배송 관리로 옮겼다** (2026-09-22, SHIPPING_PLAN §2.2.1).
	오더 관리에 남는 배송 관련 동작은 없다 — 주문확인을 하면 그 주문이 배송 관리 목록에 선다.
	트리거 액션과 사가는 아래에서 지웠다.
*/
/** 배송 업데이트 — 선택한 주문의 FedEx 추적을 갱신하고 목록을 다시 읽는다 */
function* triggerUpdateOrderTransitSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
		yield put(
			shipmentActions.TRIGGER_TRACK({
				shipmentIds: [],
				orderIds: selectedOrderIds,
				dispatchIds: [],
				refreshOrders: true,
			}),
		)
	} catch (error) {
		Logger.warn(error)
	}
}
/** 주문확인 한 건 (자세히 모달) — PUT /order/:id/prepare. PREPARED_AT 이 찍히면 상태가 배송 준비중이 된다 */
function* triggerPrepareOrderSaga(action: { type: string; payload: string }) {
	try {
		const res: ResType | undefined = yield call(orderApi.prepareOrder, {
			params: { orderId: action.payload },
		})
		if (isOk(res)) {
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
			yield put(
				configActions.TOAST({
					isShow: true,
					message: `주문 ${action.payload} — 배송 준비중으로 바뀌었습니다.`,
				}),
			)
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '주문확인에 실패했습니다.'),
				}),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(configActions.TOAST({ isShow: true, message: '주문확인에 실패했습니다.' }))
	} finally {
		yield put(orderActions.ACTING(false))
	}
}

/** 입금 확인 한 건 (자세히 모달) — PUT /order/:id/paid. 도매·입금 대기 주문만 서버가 받는다 */
function* triggerMarkPaidSaga(action: {
	type: string
	payload: { orderId: string; amount: number | null; ref: string }
}) {
	try {
		const res: ResType | undefined = yield call(orderApi.markOrderPaid, {
			params: { orderId: action.payload.orderId },
			body: { amount: action.payload.amount, ref: action.payload.ref },
		})
		if (isOk(res)) {
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
			yield put(
				configActions.TOAST({
					isShow: true,
					message: `주문 ${action.payload.orderId} — 입금 확인.`,
				}),
			)
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '입금 확인에 실패했습니다.'),
				}),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(configActions.TOAST({ isShow: true, message: '입금 확인에 실패했습니다.' }))
	} finally {
		yield put(orderActions.ACTING(false))
	}
}

/** 입금 확인 — 선택한 주문 전부(월말 정산 입금). 한 건이 실패해도 끝까지 돌린다 */
function* triggerOrderMarkPaidSaga() {
	try {
		const selectedOrderIds: string[] = yield select(state => state.order.selectedOrderIds)
		let okCount = 0
		let failText = ''
		for (const orderId of selectedOrderIds) {
			const res: ResType | undefined = yield call(orderApi.markOrderPaid, {
				params: { orderId },
				body: { amount: null, ref: '' },
			})
			if (isOk(res)) okCount += 1
			else if (failText === '') failText = errorText(res, '입금 확인에 실패했습니다.')
		}
		yield put(orderActions.SELECTED_ORDER_IDS([]))
		yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		yield put(
			configActions.TOAST({
				isShow: true,
				message:
					failText === ''
						? `${okCount}건 입금 확인.`
						: `${okCount}건 입금 확인, ${
								selectedOrderIds.length - okCount
						  }건 실패 — ${failText}`,
			}),
		)
	} catch (error) {
		Logger.warn(error)
	} finally {
		yield put(orderActions.ACTING(false))
	}
}

/** 오류 본문에서 서버가 붙인 코드(`meta.title`) — 409 를 구분하는 데 쓴다 */
const errorTitle = (res: ResType | undefined): string => {
	const title = (res?.data as any)?.message?.meta?.title
	return typeof title === 'string' ? title : ''
}

/** 환불 잠금 409 가 알려 주는 잠금 사유 (`meta.detail.reason`) */
const lockReasonOf = (res: ResType | undefined): string => {
	const reason = (res?.data as any)?.message?.meta?.detail?.reason
	return typeof reason === 'string' ? reason : ''
}

/**
 * 환불 한 건 (자세히 모달) — `POST /order/:id/refund` 하나만 부른다.
 *
 * 결제수단 분기(PayPal · PayVerse)도, **부르기 전 게이트웨이 조회**도 서버가 한다. 관리자가 PayPal
 * 대시보드나 PayVerse 가맹점관리자에서 이미 환불했으면 돈은 건드리지 않고 주문만 정리된다.
 * **사유는 필수**이고 주문 이력에 남는다.
 *
 * 이미 출고된 주문은 서버가 409 `[REFUND]:ALREADY-SHIPPED` 로 막는다(PAYMENT.md §8). 그 사유를
 * 스토어에 넣어 두면 모달이 "그래도 환불" 을 한 번 더 묻고, 그때는 `overrideLock` 을 실어 다시 부른다.
 */
function* triggerRefundOneSaga(action: {
	type: string
	payload: { orderId: string; reason: string; overrideLock?: boolean }
}) {
	try {
		const res: ResType | undefined = yield call(orderApi.refundOrder, {
			params: { orderId: action.payload.orderId },
			body: {
				reason: action.payload.reason,
				overrideLock: action.payload.overrideLock === true,
			},
		})

		if (isOk(res)) {
			const result: any = (res?.data as any[])?.[0] ?? {}
			yield put(orderActions.REFUND_LOCK_REASON(''))
			// 돈이 실제로 오간 결말만 목록을 다시 읽는다. 접수 중·거절은 주문이 그대로다
			if (['refunded', 'alreadyRefunded'].includes(String(result.outcome))) {
				yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
			}
			yield put(
				configActions.TOAST({
					isShow: true,
					message: String(result.message ?? '환불 요청을 처리했습니다.'),
				}),
			)
		} else if (errorTitle(res) === '[REFUND]:ALREADY-SHIPPED') {
			// 모달이 이 값을 보고 "그래도 환불" 을 묻는다
			const lockReason = lockReasonOf(res)
			yield put(
				orderActions.REFUND_LOCK_REASON(
					lockReason === '' ? '이미 출고된 주문입니다.' : lockReason,
				),
			)
			yield put(
				configActions.TOAST({
					isShow: true,
					message: '이미 출고된 주문입니다. 그래도 환불할지 다시 확인합니다.',
				}),
			)
		} else {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: errorText(res, '환불에 실패했습니다.'),
				}),
			)
		}
	} catch (error) {
		Logger.warn(error)
		yield put(configActions.TOAST({ isShow: true, message: '환불에 실패했습니다.' }))
	} finally {
		yield put(orderActions.ACTING(false))
	}
}

function* mainSaga() {
	yield takeEvery(orderActions.TRIGGER_INIT_ORDER_PAGE.type, triggerInitOrderPage)
	yield takeEvery(orderActions.TRIGGER_ORDER_SEARCH.type, triggerOrderSearchSaga)
	yield takeEvery(orderActions.TRIGGER_ORDER_PREPARING.type, triggerOrderPreparingSaga)
	yield takeEvery(
		orderActions.TRIGGER_UPDATE_ORDER_TRANSIT.type,
		triggerUpdateOrderTransitSaga,
	)
	yield takeEvery(orderActions.FETCH_ORDER_ONE.type, fetchOrderOneSaga)
	yield takeEvery(orderActions.TRIGGER_PREPARE_ORDER.type, triggerPrepareOrderSaga)
	yield takeEvery(orderActions.TRIGGER_MARK_PAID.type, triggerMarkPaidSaga)
	yield takeEvery(orderActions.TRIGGER_ORDER_MARK_PAID.type, triggerOrderMarkPaidSaga)
	yield takeEvery(orderActions.TRIGGER_REFUND_ONE.type, triggerRefundOneSaga)

	yield takeEvery(orderActions.SEARCH_START_DATE.type, searchStartDateSaga)
	yield takeEvery(orderActions.SEARCH_END_DATE.type, searchEndDateSaga)
	yield takeEvery(orderActions.SEARCH_STATUS.type, searchStatusSaga)
	yield takeEvery(orderActions.SEARCH_KEYWORD.type, searchKeywordSaga)
	yield takeEvery(orderActions.SEARCH_MODE.type, searchStatusSaga)
	yield takeEvery(orderActions.SEARCH_SEASON_ID.type, searchStatusSaga)
	yield takeEvery(orderActions.SEARCH_CHANNEL.type, searchStatusSaga)

	yield takeEvery(orderActions.SET_SELECTED_YEAR.type, fetchOrderOptions)
	yield takeEvery(orderActions.IS_DELETED_INCLUDING.type, fetchOrderOptions)
	yield takeEvery(orderActions.DELETE_ORDER.type, deleteOrderSaga)
	yield takeEvery(orderActions.REGISTER_ORDER.type, registerOrderSaga)
	yield takeEvery(orderActions.UPDATE_ORDER.type, updateOrderSaga)

	yield takeEvery(orderActions.SELECTED_ORDER_IDS.type, selectedOrderIdsSaga)
}

// saga
export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
